import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "../../components/modal";
import {
  createRequestType,
  deleteRequestType,
  listRequestApplications,
  listRequestTypes,
  listRequestApproverPositions,
  listRequestRelievers,
  submitRequestApplication,
  updateRequestType,
} from "../../api/requests";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";

const REQUESTS_PAGE_CACHE_KEY = "cehr_requests_page_v1";

const FIELD_TYPES = [
  { label: "Short text", value: "short_text" },
  { label: "Long text", value: "long_text" },
  { label: "Number", value: "number" },
  { label: "Date", value: "date" },
  { label: "Single select", value: "single_select" },
  { label: "Multi select", value: "multi_select" },
  { label: "File", value: "file" },
];

const blankField = () => ({
  label: "",
  fieldType: "short_text",
  helpText: "",
  isRequired: false,
  optionsText: "",
});

const emptyRequestForm = {
  category: "Other",
  includeDepartmentHead: false,
  requiresReliever: false,
  approverPositionIds: [],
  requestName: "",
  requestDescription: "",
  isActive: true,
  fields: [blankField()],
};

const emptyApplicationForm = {
  requestTypeId: "",
  applicantName: "",
  applicantEmail: "",
  applicantPhone: "",
  relieverEmployeeId: "",
  responses: {},
};

const toPayloadField = (field) => ({
  label: field.label.trim(),
  fieldType: field.fieldType,
  helpText: field.helpText?.trim() || undefined,
  isRequired: !!field.isRequired,
  options:
    field.fieldType === "single_select" || field.fieldType === "multi_select"
      ? field.optionsText
          .split(",")
          .map((option) => option.trim())
          .filter(Boolean)
      : undefined,
});

const getStatusColor = (status) => {
  if (status === "Approved") return "text-green-600 bg-green-50";
  if (status === "Declined") return "text-red-600 bg-red-50";
  return "text-amber-700 bg-amber-50";
};

export default function RequestsPage() {
  const recentApplicationsRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    companyAccount,
    currentEmployee,
    currentEmployeeName,
    currentEmployeeEmail,
  } = useCurrentEmployee();
  const cachedData = useMemo(
    () =>
      readSessionCache(REQUESTS_PAGE_CACHE_KEY, {
        requestTypes: [],
        myApplications: [],
      }),
    [],
  );
  const [requestTypes, setRequestTypes] = useState(
    cachedData?.requestTypes || [],
  );
  const [myApplications, setMyApplications] = useState(
    cachedData?.myApplications || [],
  );
  const [loading, setLoading] = useState(
    !(cachedData?.requestTypes?.length || cachedData?.myApplications?.length),
  );
  const [saving, setSaving] = useState(false);
  const [positions, setPositions] = useState([]);
  const [relievers, setRelievers] = useState([]);
  const requestedSection = searchParams.get("section")?.toLowerCase();
  const [activeCategory, setActiveCategory] = useState(requestedSection === "other" ? "Other" : "Leave");
  useEffect(() => {
    setActiveCategory(requestedSection === "other" ? "Other" : "Leave");
  }, [requestedSection]);
  const selectCategory = (category) => {
    setActiveCategory(category);
    setSearchParams({ section: category.toLowerCase() }, { replace: true });
  };

  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [requestForm, setRequestForm] = useState(emptyRequestForm);

  const [applicationModalOpen, setApplicationModalOpen] = useState(false);
  const [applicationForm, setApplicationForm] = useState(emptyApplicationForm);
  const [submittingApplication, setSubmittingApplication] = useState(false);

  const selectedRequestType = useMemo(
    () =>
      requestTypes.find(
        (requestType) => requestType.id === applicationForm.requestTypeId,
      ),
    [requestTypes, applicationForm.requestTypeId],
  );

  const canManageRequests = companyAccount || currentEmployee?.modulePermissions?.includes("requests");
  const [workspace, setWorkspace] = useState("admin");
  const isAdminView = canManageRequests && (companyAccount || workspace === "admin");
  const isEmployeeView = !companyAccount && (!canManageRequests || workspace === "employee");

  useEffect(() => {
    if (!isEmployeeView || !currentEmployee) {
      return;
    }

    setApplicationForm((previous) => ({
      ...previous,
      applicantName: previous.applicantName || currentEmployeeName,
      applicantEmail: previous.applicantEmail || currentEmployeeEmail,
      applicantPhone:
        previous.applicantPhone ||
        currentEmployee?.phone ||
        currentEmployee?.phoneNumber ||
        "",
    }));
  }, [
    isEmployeeView,
    currentEmployee,
    currentEmployeeName,
    currentEmployeeEmail,
  ]);

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      const hasStaleData = requestTypes.length > 0 || myApplications.length > 0;

      if (!silent && !hasStaleData) {
        setLoading(true);
      }

      try {
        const [requestTypesRes, myApplicationsRes, positionsRes, relieversRes] = await Promise.all([
          listRequestTypes({ includeInactive: true }),
          listRequestApplications({ limit: 5 }),
          isAdminView ? listRequestApproverPositions() : Promise.resolve([]),
          listRequestRelievers(),
        ]);

        const nextRequestTypes = requestTypesRes?.requestTypes || [];
        const nextMyApplications = myApplicationsRes?.applications || [];

        setRequestTypes(nextRequestTypes);
        setMyApplications(nextMyApplications);
        setPositions(positionsRes || []);
        setRelievers(relieversRes || []);
        writeSessionCache(REQUESTS_PAGE_CACHE_KEY, {
          requestTypes: nextRequestTypes,
          myApplications: nextMyApplications,
        });
      } catch (error) {
        const message =
          error?.response?.data?.message || "Unable to load requests.";
        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [isAdminView, myApplications.length, requestTypes.length],
  );

  useEffect(() => {
    loadData({ silent: true });
  }, [loadData]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadData({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadData]);

  const openCreateModal = () => {
    setEditingRequest(null);
    setRequestForm({ ...emptyRequestForm, category: activeCategory });
    setRequestModalOpen(true);
  };

  const openEditModal = (requestType) => {
    setEditingRequest(requestType);
    setRequestForm({
      requestName: requestType.name || "",
      category: requestType.category || "Other",
      includeDepartmentHead: !!requestType.includeDepartmentHead,
      requiresReliever: !!requestType.requiresReliever,
      approverPositionIds: (requestType.approvalPositions || []).map((position) => position.id),
      requestDescription: requestType.description || "",
      isActive: !!requestType.isActive,
      fields:
        requestType.fields?.length > 0
          ? requestType.fields.map((field) => ({
              label: field.label || "",
              fieldType: field.fieldType || "short_text",
              helpText: field.helpText || "",
              isRequired: !!field.isRequired,
              optionsText: Array.isArray(field.options)
                ? field.options.join(", ")
                : "",
            }))
          : [blankField()],
    });
    setRequestModalOpen(true);
  };

  const handleRequestFieldChange = (index, key, value) => {
    setRequestForm((previous) => ({
      ...previous,
      fields: previous.fields.map((field, fieldIndex) =>
        fieldIndex === index ? { ...field, [key]: value } : field,
      ),
    }));
  };

  const addFormField = () => {
    setRequestForm((previous) => ({
      ...previous,
      fields: [...previous.fields, blankField()],
    }));
  };

  const removeFormField = (index) => {
    setRequestForm((previous) => ({
      ...previous,
      fields: previous.fields.filter((_, fieldIndex) => fieldIndex !== index),
    }));
  };

  const handleSaveRequestType = async () => {
    const trimmedName = requestForm.requestName.trim();
    const trimmedDescription = requestForm.requestDescription.trim();

    if (!trimmedName || !trimmedDescription) {
      toast.error("Request name and description are required.");
      return;
    }

    if (requestForm.fields.length === 0) {
      toast.error("Add at least one request form field.");
      return;
    }

    if (requestForm.fields.some((field) => !field.label.trim())) {
      toast.error("Each request field must have a label.");
      return;
    }
    if (requestForm.approverPositionIds.length === 0) {
      toast.error("Select at least one approver position.");
      return;
    }

    const payload = {
      requestName: trimmedName,
      category: requestForm.category,
      includeDepartmentHead: requestForm.includeDepartmentHead,
      requiresReliever: requestForm.category === "Leave" && requestForm.requiresReliever,
      approverPositionIds: requestForm.approverPositionIds,
      requestDescription: trimmedDescription,
      isActive: requestForm.isActive,
      fields: requestForm.fields.map(toPayloadField),
    };

    setSaving(true);
    try {
      if (editingRequest) {
        await updateRequestType(editingRequest.id, payload);
        toast.success("Request updated successfully.");
      } else {
        await createRequestType(payload);
        toast.success("Request created successfully.");
      }

      setRequestModalOpen(false);
      await loadData();
    } catch (error) {
      const message =
        error?.response?.data?.message || "Unable to save request.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRequestType = async (requestType) => {
    const confirmed = window.confirm(
      `Delete ${requestType.name}? Existing applications linked to it will also be removed.`,
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteRequestType(requestType.id);
      toast.success("Request deleted successfully.");
      await loadData();
    } catch (error) {
      const message =
        error?.response?.data?.message || "Unable to delete request.";
      toast.error(message);
    }
  };

  const openApplicationModal = (requestTypeId = "") => {
    setApplicationForm({
      ...emptyApplicationForm,
      requestTypeId,
      applicantName: currentEmployeeName || "",
      applicantEmail: currentEmployeeEmail || "",
      applicantPhone:
        currentEmployee?.phone || currentEmployee?.phoneNumber || "",
    });
    setApplicationModalOpen(true);
  };

  const handleSubmitApplication = async () => {
    if (submittingApplication) {
      return;
    }

    if (!selectedRequestType) {
      toast.error("Select a request type.");
      return;
    }

    const applicantName = applicationForm.applicantName.trim();
    const applicantEmail = applicationForm.applicantEmail.trim();

    if (!applicantName || !applicantEmail) {
      toast.error("Applicant name and email are required.");
      return;
    }

    if (selectedRequestType.requiresReliever && !applicationForm.relieverEmployeeId) {
      toast.error("Select a reliever for this leave request.");
      return;
    }

    const missingRequiredField = (selectedRequestType.fields || []).find(
      (field) => field.isRequired && !applicationForm.responses[field.id],
    );

    if (missingRequiredField) {
      toast.error(`${missingRequiredField.label} is required.`);
      return;
    }

    const responses = (selectedRequestType.fields || []).map((field) => ({
      fieldId: field.id,
      label: field.label,
      value: applicationForm.responses[field.id] || "",
    }));

    try {
      setSubmittingApplication(true);
      await submitRequestApplication({
        requestTypeId: selectedRequestType.id,
        applicantName,
        applicantEmail,
        applicantPhone: applicationForm.applicantPhone.trim() || undefined,
        relieverEmployeeId: applicationForm.relieverEmployeeId || undefined,
        responses,
      });

      toast.success("Request submitted successfully.");
      setApplicationModalOpen(false);
      await loadData();
    } catch (error) {
      const message =
        error?.response?.data?.message || "Unable to submit request.";
      toast.error(message);
    } finally {
      setSubmittingApplication(false);
    }
  };

  return (
    <div className="min-h-screen p-4 font-Nunito sm:p-6">
      {canManageRequests && !companyAccount ? <div className="mb-4 flex justify-end"><div className="inline-flex rounded-xl border bg-white p-1 shadow-sm"><button onClick={() => setWorkspace("admin")} className={`rounded-lg px-4 py-2 text-xs font-bold ${workspace === "admin" ? "bg-[#1234CD] text-white" : "text-slate-500"}`}>Admin view</button><button onClick={() => setWorkspace("employee")} className={`rounded-lg px-4 py-2 text-xs font-bold ${workspace === "employee" ? "bg-[#1234CD] text-white" : "text-slate-500"}`}>My requests</button></div></div> : null}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-2 sm:mb-6 sm:gap-3">
        <h1 className="text-2xl font-semibold text-black">Requests</h1>
        <p className="text-sm text-[#5F6B8A]">
          {isAdminView
            ? "Manage request types and review applications."
            : "Browse active request types and submit applications."}
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-2 rounded-xl bg-[#F2F4F7] p-1.5">
        {["Leave", "Other"].map((category) => (
          <button key={category} type="button" onClick={() => selectCategory(category)} className={`flex-1 rounded-lg px-4 py-3 text-sm font-semibold transition sm:flex-none ${activeCategory === category ? "bg-white text-[#1234CD] shadow-sm" : "text-[#667085]"}`}>
            {category === "Leave" ? "Leave" : "Other Requests"}
          </button>
        ))}
        <button type="button" onClick={() => recentApplicationsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} className="flex-1 rounded-lg px-4 py-3 text-sm font-semibold text-[#667085] transition hover:bg-white hover:text-[#1234CD] sm:flex-none">{isAdminView ? "Recent Applications" : "My Requests & Approvals"}</button>
      </div>

      {loading ? (
        <FetchLoadingState message="Loading requests..." className="min-h-[55vh] rounded-xl bg-white shadow-sm" />
      ) : isAdminView ? (
        <>
          <div className="flex justify-end mb-4">
            <button
              onClick={openCreateModal}
              className="px-4 py-2 rounded-lg bg-[#1234CD] text-white text-sm font-medium"
            >
              {activeCategory === "Leave" ? "Add New Leave" : "Add New Request"}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {requestTypes.filter((requestType) => (requestType.category || "Other") === activeCategory).map((requestType) => (
              <div
                key={requestType.id}
                className="bg-white rounded-xl shadow-sm p-5 border border-[#EEF0F6]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[#071C50] font-semibold text-lg">
                      {requestType.name}
                    </p>
                    <p className="text-[#667085] text-sm mt-1 line-clamp-2">
                      {requestType.description}
                    </p>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      requestType.isActive
                        ? "bg-green-50 text-green-600"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {requestType.isActive ? "Active" : "Inactive"}
                  </span>
                </div>

                <div className="mt-4 text-sm text-[#475467]">
                  <p>{requestType.totalApplications || 0} applications</p>
                  <p>{requestType.fields?.length || 0} configured fields</p>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    className="px-3 py-1.5 rounded-md bg-[#1234CD] text-white text-sm"
                    onClick={() =>
                      navigate(
                        `/requests/requestTable?requestTypeId=${requestType.id}`,
                      )
                    }
                  >
                    View Applications
                  </button>
                  <button
                    className="px-3 py-1.5 rounded-md bg-[#EEF4FF] text-[#1234CD] text-sm"
                    onClick={() => openEditModal(requestType)}
                  >
                    Edit
                  </button>
                  <button
                    className="px-3 py-1.5 rounded-md bg-red-50 text-red-600 text-sm"
                    onClick={() => handleDeleteRequestType(requestType)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div ref={recentApplicationsRef} className="mt-6 scroll-mt-4 rounded-xl border border-[#EEF0F6] bg-white p-3 shadow-sm sm:mt-8 sm:p-5">
            <h3 className="text-base font-semibold text-[#071C50] mb-3">
              Recent Applications
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[400px] table-fixed text-left sm:min-w-[640px]">
                <thead>
                  <tr className="text-sm text-[#667085] border-b">
                    <th className="w-[38%] px-1.5 py-2 sm:px-2">Request</th>
                    <th className="w-[26%] px-1.5 py-2 sm:px-2">Applicant</th>
                    <th className="w-[30%] px-1.5 py-2 sm:w-[20%] sm:px-2">Date</th>
                    <th className="w-[32%] px-1.5 py-2 sm:w-[16%] sm:px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {myApplications.map((application) => (
                    <tr
                      key={application.id}
                      onClick={() => navigate(`/requests/request-details?applicationId=${application.id}`)}
                      className="cursor-pointer border-b text-xs hover:bg-slate-50 last:border-b-0 sm:text-sm"
                    >
                      <td className="break-words px-1.5 py-2.5 sm:px-2">
                        {application.requestType?.name || "-"}
                      </td>
                      <td className="break-words px-1.5 py-2.5 sm:px-2">{application.applicantName || "-"}</td>
                      <td className="px-1.5 py-2.5 sm:px-2">
                        {application.submittedAt
                          ? new Date(
                              application.submittedAt,
                            ).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="px-1.5 py-2.5 sm:px-2">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-[11px] sm:px-2.5 sm:text-xs ${getStatusColor(application.status)}`}
                        >
                          {application.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between gap-3 sm:mb-4">
            <h2 className="text-lg font-semibold text-[#071C50]">
              Open Requests
            </h2>
            <button
              onClick={() => openApplicationModal("")}
              className="shrink-0 rounded-lg bg-[#1234CD] px-3 py-2 text-xs font-medium text-white sm:px-4 sm:text-sm"
            >
              New Application
            </button>
          </div>

          <div className="mb-6 grid grid-cols-2 gap-3 sm:mb-8 sm:gap-4 md:grid-cols-2 xl:grid-cols-3">
            {requestTypes
              .filter((requestType) => requestType.isActive)
              .filter((requestType) => (requestType.category || "Other") === activeCategory)
              .map((requestType) => (
                <div
                  key={requestType.id}
                  className="min-w-0 rounded-xl border border-[#EEF0F6] bg-white p-3 shadow-sm sm:p-5"
                >
                  <p className="break-words text-base font-semibold leading-5 text-[#071C50] sm:text-lg">
                    {requestType.name}
                  </p>
                  <p className="mt-1 line-clamp-2 text-xs leading-4 text-[#667085] sm:text-sm">
                    {requestType.description}
                  </p>
                  <button
                    className="mt-3 rounded-md bg-[#1234CD] px-2.5 py-1.5 text-xs text-white sm:mt-4 sm:px-3 sm:text-sm"
                    onClick={() => openApplicationModal(requestType.id)}
                  >
                    Apply
                  </button>
                </div>
              ))}
          </div>

          <div className="rounded-xl border border-[#EEF0F6] bg-white p-3 shadow-sm sm:p-5">
            <h3 className="text-base font-semibold text-[#071C50] mb-3">
              My Requests & Pending Approvals
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[400px] table-fixed text-left sm:min-w-[640px]">
                <thead>
                  <tr className="text-sm text-[#667085] border-b">
                    <th className="w-[38%] px-1.5 py-2 sm:px-2">Request</th>
                    <th className="w-[26%] px-1.5 py-2 sm:px-2">Applicant</th>
                    <th className="w-[30%] px-1.5 py-2 sm:w-[20%] sm:px-2">Date</th>
                    <th className="w-[32%] px-1.5 py-2 sm:w-[16%] sm:px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {myApplications.map((application) => (
                    <tr
                      key={application.id}
                      onClick={() => navigate(`/requests/request-details?applicationId=${application.id}`)}
                      className="cursor-pointer border-b text-xs hover:bg-slate-50 last:border-b-0 sm:text-sm"
                    >
                      <td className="break-words px-1.5 py-2.5 sm:px-2">
                        {application.requestType?.name || "-"}
                      </td>
                      <td className="break-words px-1.5 py-2.5 sm:px-2">{application.applicantName || "-"}</td>
                      <td className="px-1.5 py-2.5 sm:px-2">
                        {new Date(application.submittedAt).toLocaleDateString()}
                      </td>
                      <td className="px-1.5 py-2.5 sm:px-2">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-[11px] sm:px-2.5 sm:text-xs ${getStatusColor(application.status)}`}
                        >
                          {application.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <Modal
        isOpen={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
      >
        <div className="font-Outfit">
          <h3 className="text-lg font-semibold mb-4">
            {editingRequest ? `Edit ${requestForm.category === "Leave" ? "Leave" : "Request"}` : `Create New ${requestForm.category === "Leave" ? "Leave" : "Request"}`}
          </h3>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-[#344054]">Request category</label>
              <select value={requestForm.category} onChange={(event) => setRequestForm((previous) => ({ ...previous, category: event.target.value }))} className="mt-1 w-full rounded-md border border-[#D0D5DD] px-3 py-2 outline-none">
                <option value="Leave">Leave</option>
                <option value="Other">Other Request</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-[#344054]">Request Name</label>
              <input
                value={requestForm.requestName}
                onChange={(event) =>
                  setRequestForm((previous) => ({
                    ...previous,
                    requestName: event.target.value,
                  }))
                }
                className="mt-1 w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
                placeholder="ID Card Request"
              />
            </div>

            <div>
              <label className="text-sm text-[#344054]">Description</label>
              <textarea
                value={requestForm.requestDescription}
                onChange={(event) =>
                  setRequestForm((previous) => ({
                    ...previous,
                    requestDescription: event.target.value,
                  }))
                }
                className="mt-1 w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none min-h-[90px]"
                placeholder="Describe this request..."
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-[#344054]">
              <input
                type="checkbox"
                checked={requestForm.isActive}
                onChange={(event) =>
                  setRequestForm((previous) => ({
                    ...previous,
                    isActive: event.target.checked,
                  }))
                }
              />
              Open for employee submissions
            </label>

            {requestForm.category === "Leave" ? <label className="flex items-start gap-3 rounded-lg border border-[#DCE3F6] bg-[#F8FAFF] p-3 text-sm text-[#344054]"><input type="checkbox" className="mt-0.5" checked={requestForm.requiresReliever} onChange={(event) => setRequestForm((previous) => ({ ...previous, requiresReliever: event.target.checked }))} /><span><span className="block font-medium text-[#071C50]">A reliever is required</span><span className="mt-0.5 block text-xs text-[#667085]">Employees must select an active colleague when applying. The selected colleague will be notified.</span></span></label> : null}

            <div>
              <div className="mb-2">
                <p className="font-medium text-[#071C50]">Approval flow (positions)</p>
                <p className="text-xs text-[#667085]">Build the approval order for each employee request.</p>
              </div>
              <label className="mb-3 flex items-start gap-3 rounded-lg border border-[#DCE3F6] bg-[#F8FAFF] p-3 text-sm text-[#344054]">
                <input type="checkbox" className="mt-0.5" checked={requestForm.includeDepartmentHead} onChange={(event) => setRequestForm((previous) => ({ ...previous, includeDepartmentHead: event.target.checked }))} />
                <span><span className="block font-medium text-[#071C50]">Department HOD approves first</span><span className="mt-0.5 block text-xs text-[#667085]">Automatically assigns the requester&apos;s HOD as step 1. If the requester is the HOD, this step is skipped.</span></span>
              </label>
              <p className="mb-2 text-xs text-[#667085]">Select any additional positions in the exact order they must approve.</p>
              <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-[#EAECF0] p-3">
                {positions.map((position) => {
                  const selectedIndex = requestForm.approverPositionIds.indexOf(position.id);
                  return <label key={position.id} className="flex items-center justify-between gap-3 text-sm">
                    <span className="flex items-center gap-2"><input type="checkbox" checked={selectedIndex >= 0} onChange={(event) => setRequestForm((previous) => ({ ...previous, approverPositionIds: event.target.checked ? [...previous.approverPositionIds, position.id] : previous.approverPositionIds.filter((id) => id !== position.id) }))} />{position.positionName}</span>
                    {selectedIndex >= 0 ? <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#EEF2FF] text-xs font-bold text-[#1234CD]">{selectedIndex + (requestForm.includeDepartmentHead ? 2 : 1)}</span> : null}
                  </label>;
                })}
              </div>
              {requestForm.includeDepartmentHead || requestForm.approverPositionIds.length > 0 ? <div className="mt-3 space-y-2 rounded-lg bg-[#F8FAFC] p-3">{requestForm.includeDepartmentHead ? <div className="flex items-center gap-2 rounded-md border border-[#C7D2FE] bg-[#EEF2FF] px-3 py-2 text-sm"><span className="font-bold text-[#1234CD]">1</span><span className="flex-1 font-medium text-[#071C50]">Requester&apos;s department HOD</span><span className="text-xs text-[#667085]">Automatic</span></div> : null}{requestForm.approverPositionIds.map((id, index) => { const position = positions.find((item) => item.id === id); return <div key={id} className="flex items-center gap-2 rounded-md border border-[#EAECF0] bg-white px-3 py-2 text-sm"><span className="font-bold text-[#1234CD]">{index + (requestForm.includeDepartmentHead ? 2 : 1)}</span><span className="flex-1">{position?.positionName || "Position"}</span><button type="button" aria-label="Move approver earlier" disabled={index === 0} onClick={() => setRequestForm((previous) => { const next = [...previous.approverPositionIds]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return { ...previous, approverPositionIds: next }; })}>Up</button><button type="button" aria-label="Move approver later" disabled={index === requestForm.approverPositionIds.length - 1} onClick={() => setRequestForm((previous) => { const next = [...previous.approverPositionIds]; [next[index + 1], next[index]] = [next[index], next[index + 1]]; return { ...previous, approverPositionIds: next }; })}>Down</button></div>; })}</div> : null}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-[#071C50]">
                  Request Form Fields
                </p>
                <button
                  onClick={addFormField}
                  className="text-sm text-[#1234CD]"
                  type="button"
                >
                  + Add Field
                </button>
              </div>

              <div className="space-y-3 max-h-[260px] overflow-auto pr-1">
                {requestForm.fields.map((field, index) => (
                  <div
                    key={`${index}-${field.fieldType}`}
                    className="border border-[#EAECF0] rounded-lg p-3"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        value={field.label}
                        onChange={(event) =>
                          handleRequestFieldChange(
                            index,
                            "label",
                            event.target.value,
                          )
                        }
                        className="border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
                        placeholder="Field label"
                      />

                      <select
                        value={field.fieldType}
                        onChange={(event) =>
                          handleRequestFieldChange(
                            index,
                            "fieldType",
                            event.target.value,
                          )
                        }
                        className="border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
                      >
                        {FIELD_TYPES.map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <input
                      value={field.helpText}
                      onChange={(event) =>
                        handleRequestFieldChange(
                          index,
                          "helpText",
                          event.target.value,
                        )
                      }
                      className="mt-3 border border-[#D0D5DD] rounded-md px-3 py-2 outline-none w-full"
                      placeholder="Help text (optional)"
                    />

                    {(field.fieldType === "single_select" ||
                      field.fieldType === "multi_select") && (
                      <input
                        value={field.optionsText}
                        onChange={(event) =>
                          handleRequestFieldChange(
                            index,
                            "optionsText",
                            event.target.value,
                          )
                        }
                        className="mt-3 border border-[#D0D5DD] rounded-md px-3 py-2 outline-none w-full"
                        placeholder="Options separated by commas"
                      />
                    )}

                    <div className="mt-3 flex items-center justify-between">
                      <label className="text-sm text-[#344054] flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={field.isRequired}
                          onChange={(event) =>
                            handleRequestFieldChange(
                              index,
                              "isRequired",
                              event.target.checked,
                            )
                          }
                        />
                        Required
                      </label>

                      {requestForm.fields.length > 1 && (
                        <button
                          type="button"
                          className="text-sm text-red-600"
                          onClick={() => removeFormField(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                className="px-4 py-2 rounded-md border border-[#D0D5DD]"
                onClick={() => setRequestModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-md bg-[#1234CD] text-white"
                onClick={handleSaveRequestType}
                disabled={saving}
              >
                {saving
                  ? "Saving..."
                  : editingRequest
                    ? "Update Request"
                    : "Create Request"}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={applicationModalOpen}
        onClose={() => {
          if (!submittingApplication) {
            setApplicationModalOpen(false);
          }
        }}
      >
        <div className="font-Outfit">
          <h3 className="text-lg font-semibold mb-4">Apply for Request</h3>

          <div className="space-y-4">
            <select
              value={applicationForm.requestTypeId}
              onChange={(event) =>
                setApplicationForm((previous) => ({
                  ...previous,
                  requestTypeId: event.target.value,
                  responses: {},
                }))
              }
              className="w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
            >
              <option value="">Select request type</option>
              {requestTypes
                .filter((requestType) => requestType.isActive)
                .map((requestType) => (
                  <option
                    key={requestType.id}
                    value={requestType.id}
                  >
                    {requestType.name}
                  </option>
                ))}
            </select>

            <input
              value={applicationForm.applicantName}
              onChange={(event) =>
                setApplicationForm((previous) => ({
                  ...previous,
                  applicantName: event.target.value,
                }))
              }
              className="w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
              placeholder="Applicant full name"
            />

            <input
              value={applicationForm.applicantEmail}
              onChange={(event) =>
                setApplicationForm((previous) => ({
                  ...previous,
                  applicantEmail: event.target.value,
                }))
              }
              className="w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
              placeholder="Applicant email"
            />

            <input
              value={applicationForm.applicantPhone}
              onChange={(event) =>
                setApplicationForm((previous) => ({
                  ...previous,
                  applicantPhone: event.target.value,
                }))
              }
              className="w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
              placeholder="Applicant phone (optional)"
            />

            {selectedRequestType?.requiresReliever ? <label className="block text-sm text-[#344054]">Reliever *<select required value={applicationForm.relieverEmployeeId} onChange={(event) => setApplicationForm((previous) => ({ ...previous, relieverEmployeeId: event.target.value }))} className="mt-1 w-full rounded-md border border-[#D0D5DD] px-3 py-2 outline-none"><option value="">Select the colleague who will provide cover</option>{relievers.map((employee) => <option key={employee.id} value={employee.id}>{employee.firstName} {employee.lastName}{employee.department?.name ? ` — ${employee.department.name}` : ""}</option>)}</select></label> : null}

            {selectedRequestType?.fields?.map((field) => (
              <div key={field.id}>
                <label className="text-sm text-[#344054]">
                  {field.label}
                  {field.isRequired ? " *" : ""}
                </label>

                {field.fieldType === "long_text" ? (
                  <textarea
                    className="mt-1 w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none min-h-[80px]"
                    value={applicationForm.responses[field.id] || ""}
                    onChange={(event) =>
                      setApplicationForm((previous) => ({
                        ...previous,
                        responses: {
                          ...previous.responses,
                          [field.id]: event.target.value,
                        },
                      }))
                    }
                  />
                ) : field.fieldType === "single_select" ? (
                  <select
                    className="mt-1 w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
                    value={applicationForm.responses[field.id] || ""}
                    onChange={(event) =>
                      setApplicationForm((previous) => ({
                        ...previous,
                        responses: {
                          ...previous.responses,
                          [field.id]: event.target.value,
                        },
                      }))
                    }
                  >
                    <option value="">Select option</option>
                    {(field.options || []).map((option) => (
                      <option
                        key={option}
                        value={option}
                      >
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={
                      field.fieldType === "date"
                        ? "date"
                        : field.fieldType === "number"
                          ? "number"
                          : "text"
                    }
                    className="mt-1 w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
                    value={applicationForm.responses[field.id] || ""}
                    onChange={(event) =>
                      setApplicationForm((previous) => ({
                        ...previous,
                        responses: {
                          ...previous.responses,
                          [field.id]: event.target.value,
                        },
                      }))
                    }
                  />
                )}
              </div>
            ))}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="px-4 py-2 rounded-md border border-[#D0D5DD] disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => setApplicationModalOpen(false)}
                disabled={submittingApplication}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-[#1234CD] text-white disabled:cursor-wait disabled:bg-slate-300"
                onClick={handleSubmitApplication}
                disabled={submittingApplication}
              >
                {submittingApplication ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-white" />
                    Submitting...
                  </>
                ) : (
                  "Submit Request"
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
